import { useMemo } from 'react';

import {
  REPORTING_NONE_KEY,
  ReportingBucketCounts,
  ReportingGroupLevel,
  WeeklyAdoptionLevelRow,
} from '@eluve/api-contract';
import {
  Button,
  Icon,
  LoadingIcon,
  Progress,
  Stack,
  Text,
} from '@eluve/components';

import {
  ChildrenState,
  ParentScope,
  RowLabelBlock,
  TableSort,
} from './AdoptionTable';
import { UserCountBadge, UserDetailsHover } from './UserRowExtras';

const COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'total', label: 'Scheduled' },
  { key: 'completed', label: 'Completed' },
  { key: 'signed', label: 'Signed' },
  { key: 'adoption', label: 'Completion %' },
  { key: 'acceptance', label: 'Note Signed %' },
];

const meterColor = (pct: number) => {
  if (pct >= 70) {
    return 'bg-bg-positive-strong-primary';
  }
  if (pct >= 55) {
    return 'bg-bg-warning-strong-primary';
  }
  return 'bg-bg-negative-strong-primary';
};

const ratio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? Math.round((numerator / denominator) * 100) : null;

/** Whole-window rollup of a row's per-bucket counts. */
const sumCounts = (
  cells: Array<{ counts?: ReportingBucketCounts }>,
): ReportingBucketCounts =>
  cells.reduce(
    (sum, cell) => ({
      total: sum.total + (cell.counts?.total ?? 0),
      started: sum.started + (cell.counts?.started ?? 0),
      completed: sum.completed + (cell.counts?.completed ?? 0),
      signed: sum.signed + (cell.counts?.signed ?? 0),
    }),
    { total: 0, started: 0, completed: 0, signed: 0 },
  );

type AggregateValues = {
  counts: ReportingBucketCounts;
  /** Completion % — completed / scheduled appointments */
  adoption: number | null;
  /** Note signed % — signed / completed appointments */
  acceptance: number | null;
};

const toValues = (counts: ReportingBucketCounts): AggregateValues => ({
  counts,
  adoption: ratio(counts.completed, counts.total),
  acceptance: ratio(counts.signed, counts.completed),
});

const columnValue = (values: AggregateValues, key: string): number | null => {
  switch (key) {
    case 'total':
      return values.counts.total;
    case 'completed':
      return values.counts.completed;
    case 'signed':
      return values.counts.signed;
    case 'adoption':
      return values.adoption;
    case 'acceptance':
      return values.acceptance;
    default:
      return null;
  }
};

const PctCell: React.FC<{ pct: number | null }> = ({ pct }) =>
  pct == null ? (
    <Text color="tertiary">—</Text>
  ) : (
    <div className="flex items-center justify-end gap-2">
      <Progress
        className="h-2 w-16"
        value={pct}
        indicatorClassName={meterColor(pct)}
      />
      <Text size="sm" weight="medium" className="tabular-nums">
        {pct}%
      </Text>
    </div>
  );

type FlatRow =
  | {
      kind: 'data';
      key: string;
      label: string;
      sublabel: string | null;
      values: AggregateValues;
      userCount?: number;
      details?: WeeklyAdoptionLevelRow['details'];
      depth: number;
      nodeId: string;
      isLeaf: boolean;
      isExpanded: boolean;
      childScope: ParentScope;
    }
  | { kind: 'loading' | 'error'; nodeId: string; depth: number };

/**
 * Whole-window rollup per Group by value: one row per dimension value with
 * the summed counts plus the two headline ratios, drilling down the same
 * Group by hierarchy as the breakdown — individual providers as the leaves.
 */
export const AggregateTable: React.FC<{
  rows: WeeklyAdoptionLevelRow[];
  /** Same hierarchy as the breakdown, provider leaf included */
  levels: ReportingGroupLevel[];
  firstColumnLabel: string;
  expanded: Set<string>;
  childrenByNode: Map<string, ChildrenState>;
  onToggle: (nodeId: string, depth: number, scope: ParentScope) => void;
  sort: TableSort | null;
  onSortChange: (next: TableSort | null) => void;
}> = ({
  rows,
  levels,
  firstColumnLabel,
  expanded,
  childrenByNode,
  onToggle,
  sort,
  onSortChange,
}) => {
  const flatRows = useMemo<FlatRow[]>(() => {
    const sortRows = (
      input: Array<WeeklyAdoptionLevelRow & { values: AggregateValues }>,
    ) => {
      if (!sort) {
        return input;
      }
      return [...input].sort((a, b) => {
        // '(none)' rows stay pinned last regardless of direction
        const aNone = a.key === REPORTING_NONE_KEY;
        const bNone = b.key === REPORTING_NONE_KEY;
        if (aNone !== bNone) {
          return aNone ? 1 : -1;
        }
        let cmp: number;
        if (sort.key === 'name') {
          cmp = a.label.localeCompare(b.label);
        } else {
          const av = columnValue(a.values, sort.key);
          const bv = columnValue(b.values, sort.key);
          if ((av == null) !== (bv == null)) {
            return av == null ? 1 : -1;
          }
          cmp = av == null || bv == null ? 0 : av - bv;
        }
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    };
    const withValues = (input: WeeklyAdoptionLevelRow[]) =>
      input.map((row) => ({ ...row, values: toValues(sumCounts(row.cells)) }));
    const out: FlatRow[] = [];
    const walk = (
      row: WeeklyAdoptionLevelRow & { values: AggregateValues },
      depth: number,
      parent: { scope: ParentScope; id: string },
    ) => {
      const level = levels[depth]!;
      const nodeId = `${parent.id}${level}:${row.key}`;
      const isLeaf = depth === levels.length - 1;
      const isExpanded = expanded.has(nodeId);
      const childScope: ParentScope = {
        ...parent.scope,
        ...(level === 'location' ? { parentLocationId: row.key } : {}),
        ...(level === 'specialty' ? { parentSpecialty: row.key } : {}),
        ...(level === 'microphone' ? { parentMicrophone: row.key } : {}),
        ...(level === 'template' ? { parentTemplateId: row.key } : {}),
      };
      out.push({
        kind: 'data',
        key: row.key,
        label: row.label,
        sublabel: row.sublabel,
        values: row.values,
        userCount: row.userCount,
        details: row.details,
        depth,
        nodeId,
        isLeaf,
        isExpanded,
        childScope,
      });
      if (isLeaf || !isExpanded) {
        return;
      }
      const children = childrenByNode.get(nodeId);
      if (!children || children.status === 'loading') {
        out.push({ kind: 'loading', nodeId, depth: depth + 1 });
      } else if (children.status === 'error') {
        out.push({ kind: 'error', nodeId, depth: depth + 1 });
      } else {
        sortRows(withValues(children.rows)).forEach((child) =>
          walk(child, depth + 1, { scope: childScope, id: `${nodeId}/` }),
        );
      }
    };
    sortRows(withValues(rows)).forEach((row) =>
      walk(row, 0, { scope: {}, id: '' }),
    );
    return out;
  }, [rows, levels, expanded, childrenByNode, sort]);

  // Click cycles: default direction → flipped → cleared
  const handleSortClick = (key: string) => {
    const defaultDir: TableSort['dir'] = key === 'name' ? 'asc' : 'desc';
    if (sort?.key !== key) {
      onSortChange({ key, dir: defaultDir });
    } else if (sort.dir === defaultDir) {
      onSortChange({ key, dir: defaultDir === 'asc' ? 'desc' : 'asc' });
    } else {
      onSortChange(null);
    }
  };
  const sortIndicator = (key: string) =>
    sort?.key === key ? (
      <Icon name={sort.dir === 'asc' ? 'ArrowUp' : 'ArrowDown'} size="xs" />
    ) : (
      <Icon
        name="ArrowUpDown"
        size="xs"
        className="opacity-0 group-hover:opacity-60"
      />
    );

  const hasExpanders = levels.length > 1;

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-border-primary">
      <table className="w-full min-w-[880px]">
        <thead>
          <tr className="border-b border-border-primary">
            <th className="p-0">
              <button
                className="group flex w-full items-center gap-1 px-4 py-2.5 text-left"
                aria-label={`Sort by ${firstColumnLabel}`}
                onClick={() => handleSortClick('name')}
              >
                <Text size="sm" color="tertiary" weight="medium">
                  {firstColumnLabel}
                </Text>
                {sortIndicator('name')}
              </button>
            </th>
            {COLUMNS.map((column) => (
              <th key={column.key} className="w-44 p-0">
                <button
                  className="group flex w-full items-center justify-end gap-1 px-4 py-2.5 text-right"
                  aria-label={`Sort by ${column.label}`}
                  onClick={() => handleSortClick(column.key)}
                >
                  <Text size="sm" color="tertiary" weight="medium">
                    {column.label}
                  </Text>
                  {sortIndicator(column.key)}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {flatRows.map((flat) => {
            const rowKey =
              flat.kind === 'data'
                ? flat.nodeId
                : `${flat.nodeId}:${flat.kind}`;
            if (flat.kind !== 'data') {
              return (
                <tr
                  key={rowKey}
                  className="border-b border-border-primary last:border-b-0"
                >
                  <td
                    colSpan={COLUMNS.length + 1}
                    className="px-4 py-3"
                    style={{ paddingLeft: flat.depth * 24 + 36 }}
                  >
                    {flat.kind === 'loading' ? (
                      <Stack direction="horizontal" gap={2} align="center">
                        <LoadingIcon size="sm" />
                        <Text size="sm" color="tertiary">
                          Loading breakdown…
                        </Text>
                      </Stack>
                    ) : (
                      <Text size="sm" color="tertiary">
                        Failed to load breakdown
                      </Text>
                    )}
                  </td>
                </tr>
              );
            }
            return (
              <tr
                key={rowKey}
                className="border-b border-border-primary last:border-b-0"
              >
                <td className="px-4 py-3">
                  <UserDetailsHover
                    name={flat.label}
                    details={flat.isLeaf ? flat.details : undefined}
                  >
                    <Stack
                      direction="horizontal"
                      gap={1.5}
                      align="center"
                      className="min-w-0"
                      style={{ paddingLeft: flat.depth * 24 }}
                    >
                      {!flat.isLeaf ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          aria-label={flat.isExpanded ? 'Collapse' : 'Expand'}
                          onClick={() =>
                            onToggle(flat.nodeId, flat.depth, flat.childScope)
                          }
                        >
                          <Icon
                            name={
                              flat.isExpanded ? 'ChevronDown' : 'ChevronRight'
                            }
                            size="sm"
                          />
                        </Button>
                      ) : hasExpanders ? (
                        <span className="w-6 shrink-0" />
                      ) : null}
                      <RowLabelBlock
                        label={flat.label}
                        sublabel={flat.sublabel}
                        isLeaf={flat.isLeaf}
                      />
                      {!flat.isLeaf && flat.userCount != null && (
                        <UserCountBadge count={flat.userCount} />
                      )}
                    </Stack>
                  </UserDetailsHover>
                </td>
                <td className="px-4 py-3 text-right">
                  <Text size="sm" className="tabular-nums">
                    {flat.values.counts.total.toLocaleString()}
                  </Text>
                </td>
                <td className="px-4 py-3 text-right">
                  <Text size="sm" className="tabular-nums">
                    {flat.values.counts.completed.toLocaleString()}
                  </Text>
                </td>
                <td className="px-4 py-3 text-right">
                  <Text size="sm" className="tabular-nums">
                    {flat.values.counts.signed.toLocaleString()}
                  </Text>
                </td>
                <td className="px-4 py-3">
                  <PctCell pct={flat.values.adoption} />
                </td>
                <td className="px-4 py-3">
                  <PctCell pct={flat.values.acceptance} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
